import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, CheckCircle2, Loader2, AlertCircle, SmartphoneNfc } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Images from '../Images';
import SubscriptionRequest from "../requests/vendor/SubscriptionRequest"

const VERIFICATION_RETRIES = 5;
const VERIFICATION_INTERVAL = 5000;
const INITIAL_WAIT_TIME = 15000;

const ProcessingModal = ({ 
  isProcessingOpen, 
  onComplete, 
  paymentMethod, 
  paymentDetails,
  processingSteps,
  payload
}) => {
  const navigate = useNavigate();
  const [processingStep, setProcessingStep] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState('processing');
  const [error, setError] = useState(null);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [transactionId, setTransactionId] = useState(null);
  const [shouldVerify, setShouldVerify] = useState(true);
  const [isInitiated, setIsInitiated] = useState(false);
  const verificationAttemptsRef = useRef(0);

  const defaultSteps = {
    mobile: [
      'Initiating payment',
      'Waiting for confirmation',
      'Processing payment',
      'Verifying transaction'
    ]
  };

  const steps = processingSteps || defaultSteps[paymentMethod];

  const initiatePayment = async () => {
    try {
      const response = await SubscriptionRequest.initiateSubscription(payload.phone_number, payload.network, payload.subscription_plan);
      
      if (response.data?.status_code === "000" && !response.data?.in_error) {
        return response.data.data.transaction_id;
      }
      throw new Error(response.data?.message || 'Payment initiation failed');
    } catch (err) {
      console.error('Payment initiation error:', err);
      throw new Error(err.message || 'Failed to initiate payment');
    }
  };

  const verifyPayment = async (txId) => {
    try {
      const response = await SubscriptionRequest.verifySubscription(txId);
      
      if (response.data?.status_code === "000" && !response.data?.in_error) {
        return response.data.data.status;
      }
      throw new Error(response.data?.message || 'Verification failed');
    } catch (err) {
      console.error('Verification error:', err);
      throw new Error('Failed to verify payment');
    }
  };

  // Smoother step transitions
  const moveToNextStep = useCallback(() => {
    setProcessingStep(prev => Math.min(prev + 1, steps.length - 1));
  }, [steps.length]);

  const handleVerificationRetry = useCallback(async (txId) => {
    // Increment attempt count at the start
    verificationAttemptsRef.current += 1;
    console.log(`Verification attempt ${verificationAttemptsRef.current} of ${VERIFICATION_RETRIES}`);

    // Check if we've exceeded max retries
    if (!shouldVerify || verificationAttemptsRef.current > VERIFICATION_RETRIES) {
      setShouldVerify(false);
      setPaymentStatus('pending');
      await new Promise(resolve => setTimeout(resolve, 1000));
      navigate('/payment/verify', { 
        state: {
          transactionId: txId,
          paymentDetails,
          attempts: VERIFICATION_RETRIES
        }
      });
      return;
    }
    try {
      if (paymentStatus === 'success') {
        setShouldVerify(false);
        return;
      }

      setPaymentStatus('verifying');
      setVerificationAttempts(verificationAttemptsRef.current);

      const status = await verifyPayment(txId);

      switch (status?.toLowerCase()) {
        case 'completed':
        case 'success':
          setShouldVerify(false);
          setPaymentStatus('success');
          moveToNextStep();
          await new Promise(resolve => setTimeout(resolve, 1000));
          navigate('/payment/success', { 
            state: { 
              transactionId: txId,
              paymentDetails 
            }
          });
          return;

        case 'failed':
        case 'declined':
        case 'error':
          setShouldVerify(false);
          setPaymentStatus('failed');
          await new Promise(resolve => setTimeout(resolve, 1000));
          navigate('/payment/failed', {
            state: {
              transactionId: txId,
              paymentDetails,
              error: 'Payment was declined'
            }
          });
          return;

        case 'pending':
        case 'processing':
          if (verificationAttemptsRef.current < VERIFICATION_RETRIES) {
            const timeoutId = setTimeout(() => {
              handleVerificationRetry(txId);
            }, VERIFICATION_INTERVAL);

            return () => {
              clearTimeout(timeoutId);
            };
          } else {
            setShouldVerify(false);
            setPaymentStatus('pending');
            await new Promise(resolve => setTimeout(resolve, 1000));
            navigate('/payment/verify', { 
              state: {
                transactionId: txId,
                paymentDetails,
                attempts: VERIFICATION_RETRIES
              }
            });
          }
          break;

        default:
          throw new Error(`Unknown payment status: ${status}`);
      }
    } catch (err) {
      setShouldVerify(false);
      console.error('Verification retry error:', err);
      setPaymentStatus('failed');
      setError(err.message);
      await new Promise(resolve => setTimeout(resolve, 1000));
      navigate('/payment/failed', {
        state: {
          transactionId: txId,
          paymentDetails,
          error: err.message
        }
      });
    }
  }, [navigate, paymentDetails, moveToNextStep, paymentStatus, shouldVerify]);

  useEffect(() => {
    let isSubscribed = true;

    if (isProcessingOpen && !isInitiated) {
      const processPayment = async () => {
        try {
          // Reset verification attempts at the start of new payment
          verificationAttemptsRef.current = 0;
          setIsInitiated(true);
          setShouldVerify(true);
          setProcessingStep(0);
          const txId = await initiatePayment();
          setTransactionId(txId);
          moveToNextStep();
          
          if (!isSubscribed) return;
          
          await new Promise(resolve => setTimeout(resolve, 1500));
          moveToNextStep();
          
          if (!isSubscribed) return;
          
          console.log('Waiting 30 seconds before starting verification...');
          await new Promise(resolve => setTimeout(resolve, INITIAL_WAIT_TIME));
          moveToNextStep();
          
          if (isSubscribed && shouldVerify) {
            await handleVerificationRetry(txId);
          }
        } catch (err) {
          if (isSubscribed) {
            setShouldVerify(false);
            setPaymentStatus('failed');
            setError(err.message);
          }
        }
      };

      processPayment();

      return () => {
        isSubscribed = false;
        setShouldVerify(false);
      };
    }

    if (!isProcessingOpen) {
      setIsInitiated(false);
      verificationAttemptsRef.current = 0;
    }
  }, [isProcessingOpen]);

  const getStatusColor = () => {
    switch (paymentStatus) {
      case 'success': return 'bg-green-500';
      case 'failed': return 'bg-red-500';
      case 'pending': return 'bg-yellow-500';
      default: return 'bg-[#FF6C6C]';
    }
  };

  const getStatusIcon = () => {
    switch (paymentStatus) {
      case 'success':
        return <CheckCircle2 className="w-8 h-8 text-white" />;
      case 'failed':
        return <AlertCircle className="w-8 h-8 text-white" />;
      case 'pending':
        return <SmartphoneNfc className="w-8 h-8 text-white" />;
      default:
        return null;
    }
  };

  // Enhanced animation variants
  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.9,
      transition: {
        duration: 0.2,
        ease: "easeIn"
      }
    }
  };

  return (
    <AnimatePresence>
      {isProcessingOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="flex fixed inset-0 z-50 justify-center items-center backdrop-blur-sm bg-black/50"
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="p-8 mx-4 w-full max-w-md rounded-3xl shadow-2xl backdrop-blur-xl bg-white/90"
          >
            <div className="flex justify-center mb-6">
              {paymentMethod === 'card' && paymentDetails?.cardType === 'visa' && (
                <img src={Images.visa} alt="Visa" className="object-contain h-12" />
              )}
              {paymentMethod === 'card' && paymentDetails?.cardType === 'mastercard' && (
                <img src={Images.mastercard_alt} alt="Mastercard" className="object-contain h-12" />
              )}
              {paymentMethod === 'mobile' && (
                <div className="flex justify-center items-center h-12 text-xl font-semibold text-gray-700">
                  {paymentDetails?.network}
                </div>
              )}
            </div>

            {/* Processing Animation */}
            <div className="flex flex-col items-center space-y-4">
              <motion.div
                animate={{
                  rotate: paymentStatus === 'processing' || paymentStatus === 'verifying' ? 360 : 0,
                  borderRadius: paymentStatus === 'processing' 
                    ? ["50% 50% 50% 50%", "40% 60% 60% 40%", "50% 50% 50% 50%"]
                    : "50%",
                }}
                transition={{
                  rotate: { duration: 2, repeat: Infinity, ease: "linear" },
                  borderRadius: { duration: 2, repeat: Infinity, ease: "easeInOut" },
                }}
                className={`w-16 h-16 flex items-center justify-center ${
                  paymentStatus === 'processing'
                    ? 'border-4 border-[#FF6C6C] border-t-transparent'
                    : paymentStatus === 'verifying'
                    ? 'border-4 border-violet-500 border-t-transparent'
                    : getStatusColor()
                }`}
              >
                {getStatusIcon()}
              </motion.div>
              
              <motion.h3
                className="text-xl font-semibold text-center text-gray-800"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {paymentStatus === 'success' ? 'Payment Successful!' :
                 paymentStatus === 'failed' ? 'Payment Failed' :
                 paymentStatus === 'pending' ? 'Awaiting Verification' :
                 paymentStatus === 'verifying' ? 'Verifying Payment' :
                 'Processing Your Payment'}
              </motion.h3>

              {/* Status Message */}
              {error && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-sm text-center text-red-500"
                >
                  {error}
                </motion.p>
              )}

              {/* Processing Steps */}
              <div className="space-y-2 w-full">
                {steps.map((step, index) => (
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ 
                      opacity: index <= processingStep ? 1 : 0.5,
                      x: 0,
                    }}
                    className="flex items-center space-x-3"
                  >
                    <motion.div
                      animate={{
                        scale: index === processingStep ? [1, 1.2, 1] : 1,
                      }}
                      transition={{
                        duration: 1,
                        repeat: index === processingStep ? Infinity : 0,
                      }}
                      className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        index < processingStep 
                          ? 'bg-green-500' 
                          : index === processingStep 
                          ? 'bg-violet-500' 
                          : 'bg-gray-200'
                      }`}
                    >
                      {index < processingStep ? (
                        <CheckCircle2 className="w-3 h-3 text-white" />
                      ) : index === processingStep ? (
                        <Loader2 className="w-3 h-3 text-white animate-spin" />
                      ) : null}
                    </motion.div>
                    <span className={`text-sm ${
                      index <= processingStep ? 'text-gray-800' : 'text-gray-400'
                    }`}>
                      {step}
                    </span>
                  </motion.div>
                ))}
              </div>

              {/* Security Message */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex items-center mt-4 space-x-2 text-sm text-gray-500"
              >
                <Shield className="w-4 h-4" />
                <span>
                  {paymentMethod === 'card' 
                    ? 'Your payment is protected by bank-level security'
                    : 'Your mobile money transaction is secure'}
                </span>
              </motion.div>

              {/* Transaction ID */}
              {transactionId && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-xs text-gray-400"
                >
                  Transaction ID: {transactionId}
                </motion.p>
              )}
            </div>

            {/* Add close button when payment fails */}
            {paymentStatus === 'failed' && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="px-4 py-2 mt-6 w-full font-medium text-gray-800 bg-gray-200 rounded-lg transition-colors hover:bg-gray-300"
                onClick={() => {
                  setIsInitiated(false);
                  onComplete?.();
                }}
              >
                Close
              </motion.button>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProcessingModal;
